export type getSoftwarePageProductRecommendationResult = {
    FIRST_Recommendation: string | undefined;
    SECOND_Recommendation: string | undefined;
    THIRD_Recommendation: string | undefined;
    FOURTH_Recommendation: string | undefined;
};

export type getSoftwareRecommendationProps = {
    jobFunction?: string | null;
    loggedInProducts: string[];
};

const categoryOneJobFunctions = [
    "engineering",
    "information technology",
    "customer service",
    "human resources",
    "data science",
    "operations",
    "finance",
    "legal"
];

const categoryTwoJobFunctions = ["product management"];

const categoryThreeJobFunctions = [
    "program management",
    "c-suite",
    "sales",
    "marketing",
    "design",
    "other"
];

// Helper functions for product recommendation logic ========================================================

/**
 * Determines the product ranking order based on job function category
 */
const getRankedProducts = (
    PRODUCT_LIST: string[],
    jobFunction: string | null
): string[] => {
    if (!jobFunction) {
        // Default: use PRODUCT_LIST order when jobFunction is null
        return [...PRODUCT_LIST];
    }

    if (categoryOneJobFunctions.includes(jobFunction)) {
        // Category One: jira, confluence, jsm, loom, jpd, rovo
        return ["jira", "confluence", "jsm", "loom", "jpd", "rovo"];
    }

    if (categoryTwoJobFunctions.includes(jobFunction)) {
        // Category Two: jira, confluence, jpd, loom, rovo, jsm
        return ["jira", "confluence", "jpd", "loom", "rovo", "jsm"];
    }

    if (categoryThreeJobFunctions.includes(jobFunction)) {
        // Category Three: jira, confluence, loom, rovo, jpd, jsm
        return ["jira", "confluence", "loom", "rovo", "jpd", "jsm"];
    }

    // Fallback: use PRODUCT_LIST order for unknown job functions
    return [...PRODUCT_LIST];
};

/**
 * Applies special handling for Category One: when 1 product is owned,
 * prioritizes rovo over jpd by swapping their positions
 */
const applyCategoryOneSwap = (
    filteredProductData: string[],
    jobFunction: string | null,
    normalizedLoggedInProducts: string[]
): void => {
    const shouldApplySwap =
        categoryOneJobFunctions.includes(jobFunction || "") &&
        normalizedLoggedInProducts.length === 1 &&
        filteredProductData.length >= 4;

    if (!shouldApplySwap) {
        return;
    }

    const jpdIndex = filteredProductData.indexOf("jpd");
    const rovoIndex = filteredProductData.indexOf("rovo");

    if (jpdIndex !== -1 && rovoIndex !== -1 && jpdIndex < rovoIndex) {
        // Swap jpd and rovo positions so rovo comes before jpd
        const jpdValue = filteredProductData[jpdIndex];
        const rovoValue = filteredProductData[rovoIndex];

        if (jpdValue && rovoValue) {
            filteredProductData[jpdIndex] = rovoValue;
            filteredProductData[rovoIndex] = jpdValue;
        }
    }
};

/**
 * Determines the fill order when adding owned products back
 * For Category Two and Three, prioritizes jsm and loom
 */
const getFillOrder = (
    jobFunction: string | null,
    rankedProducts: string[]
): string[] => {
    const shouldUsePrioritizedFill =
        categoryTwoJobFunctions.includes(jobFunction || "") ||
        categoryThreeJobFunctions.includes(jobFunction || "");

    if (shouldUsePrioritizedFill) {
        // Prioritize jsm and loom when filling for Category Two and Three
        return ["jira", "confluence", "jsm", "loom", "jpd", "rovo"];
    }

    return rankedProducts;
};

/**
 * Fills the filtered product data with owned products if needed
 * to reach the target of 4 products
 */
const fillWithOwnedProducts = (
    filteredProductData: string[],
    normalizedLoggedInProducts: string[],
    fillOrder: string[]
): void => {
    while (filteredProductData.length < 4) {
        const currProduct = fillOrder.find(
            (product) =>
                normalizedLoggedInProducts.includes(product.toLowerCase()) &&
                !filteredProductData.includes(product)
        );

        if (currProduct) {
            filteredProductData.push(currProduct);
        } else {
            // If no more unique products available, break to avoid infinite loop
            break;
        }
    }
};

/**
 * Main function to get recommended products based on job function and owned products
 * Returns up to 4 products, prioritizing products the user doesn't own
 */
export const getFilteredProductData = (
    PRODUCT_LIST: string[],
    jobFunction: string | null,
    loggedInProducts: string[]
): string[] => {
    const normalizedLoggedInProducts = loggedInProducts.map((product) =>
        product.toLowerCase()
    );

    const rankedProducts = getRankedProducts(PRODUCT_LIST, jobFunction);

    // Get products NOT in listed products for logged in user
    const filteredProductData = rankedProducts.filter(
        (product) => !normalizedLoggedInProducts.includes(product.toLowerCase())
    );

    // Apply Category One special swap if applicable
    applyCategoryOneSwap(
        filteredProductData,
        jobFunction,
        normalizedLoggedInProducts
    );

    // If we have 4 or more products not owned, return first 4
    if (filteredProductData.length >= 4) {
        return filteredProductData.slice(0, 4);
    }

    // Fill with owned products if needed
    const fillOrder = getFillOrder(jobFunction, rankedProducts);
    fillWithOwnedProducts(
        filteredProductData,
        normalizedLoggedInProducts,
        fillOrder
    );

    return filteredProductData;
};

// End helper function to get recommended products ========================================================

/**
 * Custom code to get recommended products based on job function and owned products
 * Returns an array of up to 4 recommended products
 */
export const getSoftwarePageProductRecommendation = ({
    jobFunction = null,
    loggedInProducts
}: getSoftwareRecommendationProps): getSoftwarePageProductRecommendationResult => {
    const PRODUCT_LIST = ["jira", "confluence", "jsm", "loom", "jpd", "rovo"];

    // Convert empty string to null to match getFilteredProductData's expected type
    const normalizedJobFunction = jobFunction || null;
    const recommendedProducts = getFilteredProductData(
        PRODUCT_LIST,
        normalizedJobFunction,
        loggedInProducts
    );

    return {
        FIRST_Recommendation: recommendedProducts[0],
        SECOND_Recommendation: recommendedProducts[1],
        THIRD_Recommendation: recommendedProducts[2],
        FOURTH_Recommendation: recommendedProducts[3]
    };
};

export default getSoftwarePageProductRecommendation;
